import { ReactNode, useState, useEffect } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from './Button';
import { SlideOver } from './SlideOver';
import { SearchInput } from './SearchInput';
import { Select } from './Select';
import { cn } from '@/lib/utils';

export interface FilterFieldConfig {
  key: string;
  label: string;
  type: 'select';
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  className?: string;
}

interface FilterBarProps {
  /** Search input field - always visible */
  searchField: ReactNode;
  /** Additional filter fields that may collapse */
  filterFields?: ReactNode[];
  /** Filter field configurations for sidebar (simplified form) */
  filterConfigs?: FilterFieldConfig[];
  /** Current filter values (to initialize sidebar state) */
  filterValues: Record<string, any>;
  /** Callback when filters are applied from sidebar */
  onApplyFilters?: (values: Record<string, any>) => void;
  /** Callback to clear all filters */
  onClearFilters?: () => void;
  /** Whether filters have been applied (for showing active count) */
  hasActiveFilters?: boolean;
}

/**
 * Responsive filter bar that collapses into a sidebar on smaller screens
 *
 * Breakpoint logic:
 * - More than 3 total fields (including search): collapse at xl (1280px)
 * - 2-3 total fields: collapse at lg (1024px)
 * - Only search field: never collapse
 */
export function FilterBar({
  searchField,
  filterFields = [],
  filterConfigs = [],
  filterValues,
  onApplyFilters,
  onClearFilters,
  hasActiveFilters
}: FilterBarProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [localFilterValues, setLocalFilterValues] = useState<Record<string, any>>({});

  // Count total fields (search + filters)
  const totalFields = 1 + filterFields.length;

  // Determine collapse breakpoint based on field count
  // > 3 fields: collapse at xl (1280px) - same as user menu
  // 2-3 fields: collapse at lg (1024px) - one size smaller
  // 1 field (just search): never collapse
  const buttonShowClass = totalFields > 3 ? 'flex xl:hidden' : totalFields > 1 ? 'flex lg:hidden' : 'hidden';
  const fieldsShowClass = totalFields > 3 ? 'hidden xl:flex' : totalFields > 1 ? 'hidden lg:flex' : 'hidden';

  // Initialize local state when sidebar opens
  useEffect(() => {
    if (isSidebarOpen) {
      setLocalFilterValues({ ...filterValues });
    }
  }, [isSidebarOpen, filterValues]);

  // Close sidebar when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      const breakpoint = totalFields > 3 ? 1280 : 1024;
      if (window.innerWidth >= breakpoint) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [totalFields]);

  const handleLocalFilterChange = (key: string, value: any) => {
    setLocalFilterValues(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    onApplyFilters?.(localFilterValues);
    setIsSidebarOpen(false);
  };

  const handleClearFilters = () => {
    onClearFilters?.();
    setIsSidebarOpen(false);
  };

  if (filterFields.length === 0) {
    // Just show search field if no filters
    return <div className="flex items-end gap-3">{searchField}</div>;
  }

  return (
    <>
      <div className="flex items-end gap-3">
        {/* Search field - always visible */}
        <div className="min-w-[200px] flex-1">
          {searchField}
        </div>

        {/* Filter button - shown on mobile/tablet */}
        <div className={buttonShowClass}>
          <Button
            variant="secondary"
            onClick={() => setIsSidebarOpen(true)}
            className="whitespace-nowrap"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 flex h-2 w-2 rounded-full bg-primary" />
            )}
          </Button>
        </div>

        {/* Filter fields - shown on desktop */}
        <div className={cn(fieldsShowClass, 'items-end gap-3')}>
          {filterFields.map((field, index) => (
            <div key={index}>{field}</div>
          ))}
        </div>
      </div>

      {/* Filter sidebar - shown when button clicked on mobile/tablet */}
      <SlideOver
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        title="Filters"
        description="Refine your results"
      >
        <div className="space-y-4">
          {/* Search field in sidebar */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Search
            </label>
            <SearchInput
              value={localFilterValues.search ?? ''}
              onChange={(value) => handleLocalFilterChange('search', value)}
              placeholder="Search..."
              debounceMs={0}
            />
          </div>

          {/* Filter fields in sidebar - simplified controlled form */}
          {filterConfigs.map((config) => (
            <div key={config.key}>
              <Select
                label={config.label}
                value={localFilterValues[config.key] ?? ''}
                onChange={(e) => handleLocalFilterChange(config.key, e.target.value)}
                options={config.options}
                disabled={config.disabled}
              />
            </div>
          ))}
        </div>

        {/* Footer with action buttons */}
        <div className="mt-6 pt-6 border-t border-border flex gap-3">
          {onClearFilters && (
            <Button
              variant="ghost"
              onClick={handleClearFilters}
              className="flex-1"
            >
              Clear Filters
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleApplyFilters}
            className="flex-1"
          >
            Apply
          </Button>
        </div>
      </SlideOver>
    </>
  );
}
